import { usePrintMutation } from 'store/services/helios/printConfigurations';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import mime from 'mime';

import { getRowId } from 'components/common/Table/tableFunctions';
import { downloadBase64File } from 'utils/file/downloadFile';
import { getExtensionFromMimeType, ensureFileNameHasExtension, sanitizeFileName } from 'utils/file/fileUtils';
import { isLinkingConfiguration } from 'utils/helios/printConfigurationLink';
import type { PrintConfiguration } from 'types/templates';

export const useHeliosPrint = () => {
  const { t } = useTranslation();
  const [print] = usePrintMutation();
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  const handleHeliosPrintConfiguration = useCallback(
    async ({ printConfiguration, selectedRows: selectedRowIds }: { printConfiguration: PrintConfiguration; selectedRows: string[] }) => {
      if (isLinkingConfiguration(printConfiguration) && selectedRowIds.length !== 1) {
        toast.error(t('helios.templates.printConfigurations.linkToRelation.singleRecordOnly'));
        return;
      }

      try {
        setIsPrintLoading(true);
        const records = selectedRowIds.map((rowId) => Number(getRowId(rowId)));

        const result = await print({
          configurationId: printConfiguration.id,
          records,
        });

        if ('error' in result) {
          throw new Error('Print request failed');
        }

        if (!result.data) {
          throw new Error('Missing print payload');
        }

        const { fileContent, fileName, contentType } = result.data;

        if (!fileContent) {
          throw new Error('Missing file content');
        }

        const sanitizedResponseName = sanitizeFileName(fileName ?? '');
        const fallbackName = sanitizeFileName(printConfiguration.name ?? '');
        const baseName = sanitizedResponseName || fallbackName || 'print';
        const finalFileName = ensureFileNameHasExtension(baseName, contentType);
        const resolvedContentType = contentType?.trim() || mime.getType(finalFileName) || 'application/octet-stream';

        downloadBase64File({
          base64: fileContent,
          fileName: finalFileName,
          extension: getExtensionFromMimeType(contentType),
          mimeType: resolvedContentType,
        });

        toast.success(t('helios.templates.printConfigurations.tooltips.printSuccess'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in print configuration:', error);
        toast.error(t('common.error'));
      } finally {
        setIsPrintLoading(false);
      }
    },
    [print, t]
  );

  return { handleHeliosPrintConfiguration, isPrintLoading };
};
